import { Routes } from '@angular/router';
import { authGuard } from './services/guards/auth.guard';
import { readOnlyRouteGuard } from './services/guards/read-only-rout.guard';
import { canDeactivateGuard } from './services/guards/can-deactivate.guard';
import { signupInviteGuard } from './services/guards/signup-invite.guard';
import { committeeManageGuard } from './services/guards/committee-manage.guard';
import { companyRegistrationOpenGuard } from './services/guards/company-registration-open.guard';
import { askTawtheiqRouteGuard } from './services/guards/ask-tawtheiq.guard';

export const routes: Routes = [
  {
    path: 'login',
    loadComponent: () =>
      import('./pages/auth/auth.component').then((m) => m.AuthComponent),
  },
  {
    path: 'sign-up',
    canActivate: [signupInviteGuard],
    // Holds a company administrator on the page until their profile is complete.
    canDeactivate: [canDeactivateGuard],
    loadComponent: () =>
      import('./pages/sign-up/sign-up').then((m) => m.SignUp),
  },
  {
    path: 'forget-password',
    loadComponent: () =>
      import('./pages/forget-password/forget-password').then(
        (m) => m.ForgetPassword,
      ),
  },
  {
    path: 'company-registration',
    canActivate: [companyRegistrationOpenGuard],
    // Holds the administrator on the wizard once their account exists, until the company is created.
    canDeactivate: [canDeactivateGuard],
    loadComponent: () =>
      import('./pages/company-registration-v2/company-registration-component/company-registration-component').then(
        (m) => m.CompanyRegistrationComponent,
      ),
  },
  {
    path: 'verify/:minuteId',
    loadComponent: () =>
      import('./pages/minute-verification/minute-verification').then(
        (m) => m.MinuteVerification,
      ),
  },
  {
    path: 'main',
    canActivate: [authGuard],
    canActivateChild: [authGuard],
    loadComponent: () =>
      import('./layouts/main-layout/main-layout.component').then(
        (m) => m.MainLayoutComponent,
      ),
    children: [
      {
        path: 'dashboard',
        loadComponent: () =>
          import('./pages/dashboard/dashboard').then((m) => m.Dashboard),
        canActivate: [readOnlyRouteGuard],
      },
      {
        path: 'minutes/:id',
        loadComponent: () =>
          import('./pages/minutes-details/minutes-details').then(
            (m) => m.MinutesDetails,
          ),
      },
      {
        path: 'PersonalInformations',
        loadComponent: () =>
          import('./pages/personal-informations/personal-informations').then(
            (m) => m.PersonalInformations,
          ),
      },
      {
        path: 'settings',
        loadComponent: () =>
          import('./pages/settings/settings').then((m) => m.Settings),
        canActivate: [readOnlyRouteGuard],
      },
      {
        path: 'settings/org-chart',
        loadComponent: () =>
          import('./pages/settings/org-chart/org-chart.component').then(
            (m) => m.OrgChartComponent,
          ),
        canActivate: [readOnlyRouteGuard],
      },
      {
        path: 'minutes',
        loadComponent: () =>
          import('./pages/minutes/minutes').then((m) => m.Minutes),
      },
      {
        path: 'add-minute',
        loadComponent: () =>
          import('./pages/add-minute/add-minute').then((m) => m.AddMinute),
        canActivate: [readOnlyRouteGuard],
      },
      {
        path: 'committees',
        loadComponent: () =>
          import('./pages/committees/committees').then((m) => m.Committees),
      },
      {
        path: 'committees/create',
        loadComponent: () =>
          import('./pages/committees/create-wizard/create-wizard').then(
            (m) => m.CommitteesCreateWizard,
          ),
        canActivate: [readOnlyRouteGuard, committeeManageGuard],
      },
      {
        path: 'committees/:id/edit',
        loadComponent: () =>
          import('./pages/committees/create-wizard/create-wizard').then(
            (m) => m.CommitteesCreateWizard,
          ),
        canActivate: [readOnlyRouteGuard, committeeManageGuard],
      },
      {
        path: 'committees/:id',
        loadComponent: () =>
          import('./pages/committees/details/details').then(
            (m) => m.CommitteesDetails,
          ),
      },
      {
        path: 'notifications',
        loadComponent: () =>
          import('./pages/notifications/notifications').then(
            (m) => m.Notifications,
          ),
      },
      {
        path: 'actions',
        loadComponent: () =>
          import('./pages/actions/actions').then((m) => m.Actions),
        canActivate: [readOnlyRouteGuard],
      },
      {
        path: 'reports',
        loadComponent: () =>
          import('./pages/reports/reports').then((m) => m.Reports),
        canActivate: [readOnlyRouteGuard],
      },
      {
        path: 'mom-reports',
        loadComponent: () =>
          import('./pages/mom-reports/mom-reports').then(
            (m) => m.MomReportsComponent,
          ),
        canActivate: [readOnlyRouteGuard],
      },
      {
        path: 'meetings',
        loadComponent: () =>
          import('./pages/meetings/meetings').then((m) => m.Meetings),
        canActivate: [readOnlyRouteGuard],
      },
      {
        path: 'search-results',
        loadComponent: () =>
          import('./pages/search-results/search-results').then(
            (m) => m.SearchResults,
          ),
        canActivate: [readOnlyRouteGuard],
      },
      // My Network is hidden from the product: with its route commented out, /main/my-network
      // falls through to the dashboard ('**' below). Uncomment to bring the page back.
      // {
      //   path: 'my-network',
      //   loadComponent: () =>
      //     import('./pages/my-network/my-network').then((m) => m.MyNetwork),
      //   canActivate: [readOnlyRouteGuard],
      // },
      {
        path: 'copilot',
        loadComponent: () =>
          import('./pages/copilot/copilot').then((m) => m.CopilotPage),
        canActivate: [askTawtheiqRouteGuard],
      },
      {
        path: 'meet/:roomId',
        loadComponent: () =>
          import('./pages/meet/meet').then((m) => m.MeetPage),
      },
      {
        path: 'meeting-bot-admin',
        loadComponent: () =>
          import('./pages/meeting-bot-admin/meeting-bot-admin').then(
            (m) => m.MeetingBotAdmin,
          ),
        canActivate: [readOnlyRouteGuard],
      },
      // The Record page's tabs are separate routes rather than one route with query params, so
      // each tab has its own URL (/main/record, /main/url-meet, /main/history). They share
      // `data.tabGroup` so TabGroupRouteReuseStrategy keeps the same LiveTranscription instance
      // mounted across the three — a real route change per tab would otherwise tear down and
      // recreate it, destroying an in-progress recording that lives only in that component.
      {
        path: 'record',
        loadComponent: () =>
          import('./pages/live-transcription/live-transcription').then(
            (m) => m.LiveTranscription,
          ),
        data: { tabGroup: 'record-tabs' },
        canActivate: [readOnlyRouteGuard],
        canDeactivate: [canDeactivateGuard],
      },
      {
        path: 'url-meet',
        loadComponent: () =>
          import('./pages/live-transcription/live-transcription').then(
            (m) => m.LiveTranscription,
          ),
        data: { tabGroup: 'record-tabs' },
        canActivate: [readOnlyRouteGuard],
        canDeactivate: [canDeactivateGuard],
      },
      {
        path: 'history',
        loadComponent: () =>
          import('./pages/live-transcription/live-transcription').then(
            (m) => m.LiveTranscription,
          ),
        data: { tabGroup: 'record-tabs' },
        canActivate: [readOnlyRouteGuard],
        canDeactivate: [canDeactivateGuard],
      },
    ],
  },
  {
    path: '',
    redirectTo: 'main/dashboard',
    pathMatch: 'full',
  },
  {
    path: '**',
    redirectTo: 'main/dashboard',
  },
];
